import * as React from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Divider,
  Grid,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import {GET, POST} from "APIclient";
import {useEffect, useState} from "react";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {FirstPage, LastPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import properties from "constants/ApplicationConstants";

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666"}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const columns = [
  {field: "id", headerName: "#", width: 30, headerAlign: "left", align: "left"},
  {
    field: "empName",
    headerName: "Employee Name",
    width: 150,
    headerAlign: "left",
    align: "left"
  },
  {field: "empId", headerName: "EMP ID", width: 80, headerAlign: "left", align: "left"},
  {
    field: "AppliedOn",
    headerName: "Coff Against",
    width: 120,
    headerAlign: "left",
    align: "left"
  },
  {field: "reason", headerName: "Reason", width: 180},
  {field: "coffUsed", headerName: "Coff Used", width: 100, headerAlign: "center", align: "center"},
  {
    field: "status",
    headerName: "Status",
    width: 130,
    renderCell: params => {
      const status = params.value.toLowerCase();
      let buttonColor;
      let statusText;

      switch (status) {
        case "pending":
          buttonColor = "warning";
          statusText = "Pending";
          break;
        case "approved":
          buttonColor = "success";
          statusText = "Approved";
          break;
        case "rejected":
          buttonColor = "error";
          statusText = "Rejected";
          break;
      }

      return (
        <MDButton
          variant="contained"
          color={buttonColor}
          size="small"
          disabled
          sx={{
            textTransform: "capitalize",
            fontSize: "0.800rem",
            width: "70px",
            justifyContent: "center"
          }}
        >
          {statusText}
        </MDButton>
      );
    }
  },
  {
    field: "actions",
    headerName: "Action",
    width: 110,
    headerAlign: "left",
    align: "left",
    renderCell: params => {
      const status = params.row.status.toLowerCase();

      const isActionDisabled =
        status === "approved" || status === "rejected" || params.row.isUsed === 2;

      return [
        <Tooltip title="Approve Leave" arrow key={`approve-${params.id}`}>
          <GridActionsCellItem
            icon={<DoneIcon />}
            label="Approve"
            color="success"
            onClick={() => params.row.handleApprove(params.row)}
            disabled={isActionDisabled}
          />
        </Tooltip>,
        <Tooltip title="Reject Leave" arrow key={`reject-${params.id}`}>
          <GridActionsCellItem
            icon={<ClearIcon />}
            label="Reject"
            color="error"
            onClick={() => params.row.handleReject(params.row)}
            disabled={isActionDisabled}
          />
        </Tooltip>,
        <Tooltip title="View Details" arrow key={`view-${params.id}`}>
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View"
            color="primary"
            onClick={() => params.row.handleView(params.row)}
          />
        </Tooltip>
      ];
    }
  }
];

export default function CompensatoryRequestReceived() {
  const empId = Number(window.sessionStorage.getItem("empId"));
  const theme = useTheme();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 20});
  const [totalItems, setTotalItems] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [statusFilter, setStatusFilter] = useState("");
  const [empIdFilter, setEmpIdFilter] = useState("");
  const [empNameFilter, setEmpNameFilter] = useState("");
  const [appliedEmpIdFilter, setAppliedEmpIdFilter] = useState("");
  const [appliedEmpNameFilter, setAppliedEmpNameFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [actionType, setActionType] = useState("");
  const [reason, setReason] = useState("");
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const handleClose = () => {
    setSnackOpen(false);
  };

  const handleViewDetails = leaveRequest => {
    setSelectedLeaveRequest(leaveRequest);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLeaveRequest(null);
  };

  const handleApprove = row => {
    setReason("");
    setSelectedLeaveRequest(row);
    setActionType("approve");
    setActionModalOpen(true);
  };

  const handleReject = row => {
    setReason("");
    setSelectedLeaveRequest(row);
    setActionType("reject");
    setActionModalOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!reason) {
      setSnackMessage("Please provide a reason for the action.");
      setSnackColor("error");
      setSnackOpen(true);
      return;
    }

    try {
      const url = "manager/coffRequest";
      const payload = {
        loggedInEmpId: empId,
        action: actionType,
        reason: reason,
        checked_list: [selectedLeaveRequest.leaveRequestId]
      };

      const response = await POST(url, payload);
      if (response.status === 200) {
        setSnackMessage(response.data.message);
        setSnackColor("success");
        setSnackOpen(true);
        setLeaveRequests(prev =>
          prev.map(req =>
            req.leaveRequestId === selectedLeaveRequest.leaveRequestId
              ? {...req, status: actionType === "approve" ? "Approved" : "Rejected"}
              : req
          )
        );
        setFilteredLeaveRequests(prev =>
          prev.map(req =>
            req.leaveRequestId === selectedLeaveRequest.leaveRequestId
              ? {...req, status: actionType === "approve" ? "Approved" : "Rejected"}
              : req
          )
        );
        setActionModalOpen(false);
        setReason("");
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          setSnackMessage(error.response.data.message || "Bad request.");
          setSnackColor("error");
          setSnackOpen(true);
        } else if (error.response.status === 401 || error.response.status === 403) {
          setSnackMessage("Unauthorized or forbidden action.");
          setSnackColor("error");
          setSnackOpen(true);
        }
      } else {
        setSnackMessage("An error occurred while submitting the leave request.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
  };

  const fetchLeaveRequests = async (
    currentPage,
    currentPageSize,
    empIdFilter = "",
    empNameFilter = "",
    statusFilter = ""
  ) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      let url = `manager/leave/coffRequest?managerId=${empId}&page=${currentPage}&pageSize=${currentPageSize}`;
      if (empIdFilter) url += `&empId=${encodeURIComponent(empIdFilter)}`;
      if (empNameFilter) url += `&empName=${encodeURIComponent(empNameFilter)}`;
      if (statusFilter) {
        switch (statusFilter.toLowerCase()) {
          case "pending":
            url += `&status=1`;
            break;
          case "approved":
            url += `&status=0&approved=1`;
            break;
          case "rejected":
            url += `&status=2`;
            break;
          default:
            break;
        }
      }

      const response = await GET(url);

      if (response.status === 200) {
        const data = response.data.data.paginatedData || [];
        const totalRecords = response.data.data.totalItems || 0;
        const calculatedTotalPages = Math.ceil(totalRecords / currentPageSize) || 0;

        let index = currentPage * currentPageSize + 1;
        const formattedData = data.map(item => {
          let statusLabel = "";
          if (item.status === 1) {
            statusLabel = "Pending";
          } else if (item.approved === 1 && item.status === 0) {
            statusLabel = "Approved";
          } else if (item.status === 2) {
            statusLabel = "Rejected";
          }

          let coffUsed = "Not Used";
          if (item.isUsed === 1) {
            coffUsed = "Used";
          } else if (item.isUsed === 2) {
            coffUsed = "Expired";
          }

          return {
            id: index++,
            empId: item.empId,
            empName: `${item.firstName} ${item.lastName}`,
            mobileNo: item.mobileNo || "N/A",
            AppliedOn: dayjs(item.applyDate).format("DD MMM YYYY"),
            status: statusLabel,
            approved: item.approved,
            isUsed: item.isUsed,
            coffUsed: coffUsed,
            reason: item.reason,
            leaveRequestId: item.coffLeaveId,
            handleApprove,
            handleReject,
            handleView: handleViewDetails
          };
        });

        setLeaveRequests(formattedData);
        setFilteredLeaveRequests(formattedData);
        setTotalItems(totalRecords);
        setTotalPages(calculatedTotalPages);
      }
    } catch (error) {
      if (error.response) {
        if (
          error.response.status === 400 ||
          error.response.status === 401 ||
          error.response.status === 403
        ) {
          setSnackMessage(
            error.response.data.message || "Error fetching compensatory off requests."
          );
          setSnackColor("error");
          setSnackOpen(true);
        }
      } else {
        setSnackMessage("Error fetching compensatory off requests.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchLeaveRequests(
      page,
      pageSize,
      appliedEmpIdFilter,
      appliedEmpNameFilter,
      appliedStatusFilter
    );
  }, [page, pageSize, appliedEmpIdFilter, appliedEmpNameFilter, appliedStatusFilter]);

  const handleSearchClick = () => {
    setAppliedEmpIdFilter(empIdFilter);
    setAppliedEmpNameFilter(empNameFilter);
    setAppliedStatusFilter(statusFilter);
    setPage(0);
    setPaginationModel({page: 0, pageSize: pageSize});
    fetchLeaveRequests(0, pageSize, empIdFilter, empNameFilter, statusFilter);
  };

  const handleFilterChange = field => e => {
    if (field === "empId") setEmpIdFilter(e.target.value);
    if (field === "empName") setEmpNameFilter(e.target.value);
    if (field === "status") setStatusFilter(e.target.value);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    setPaginationModel({page: newPage, pageSize: pageSize});
    fetchLeaveRequests(
      newPage,
      pageSize,
      appliedEmpIdFilter,
      appliedEmpNameFilter,
      appliedStatusFilter
    );
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      setPaginationModel({page: lastPageIndex, pageSize: pageSize});
      await fetchLeaveRequests(
        lastPageIndex,
        pageSize,
        appliedEmpIdFilter,
        appliedEmpNameFilter,
        appliedStatusFilter
      );
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1.5}}>
                <Typography
                  sx={{
                    gridColumn: "span 3",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Compensatory Request Received
                </Typography>
                <TextField
                  sx={{gridColumn: "span 2"}}
                  label="Employee ID"
                  value={empIdFilter}
                  onChange={handleFilterChange("empId")}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  sx={{gridColumn: "span 2"}}
                  label="Employee Name"
                  value={empNameFilter}
                  onChange={handleFilterChange("empName")}
                  fullWidth
                  variant="outlined"
                />
                <FormControl sx={{gridColumn: "span 2"}}>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={statusFilter}
                    label="Status"
                    onChange={handleFilterChange("status")}
                    variant="outlined"
                    sx={{
                      height: "45px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
                  variant="contained"
                  color="info"
                  onClick={handleSearchClick}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredLeaveRequests}
              columns={columns}
              loading={isFetching}
              rowCount={totalItems}
              hideFooter
              size="small"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                fontFamily: properties.fontFamily,
                height: "450px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                }
              }}
              aria-label="a dense table"
            />

            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalItems}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={isFetching}
            />
          </Paper>
        </MDBox>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily, fontSize: "16px"}}>
              Compensatory Leave Request Details
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Coff Against"
                      value={selectedLeaveRequest.AppliedOn || "No Coff Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedLeaveRequest.reason || "No reason provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Divider sx={{my: 2}} />
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseModal} variant="contained">
              Close
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={actionModalOpen}
          onClose={() => {
            setActionModalOpen(false);
            setReason("");
            setSelectedLeaveRequest(null);
            setActionType("");
          }}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily, fontSize: "16px"}}>
              {`${actionType === "approve" ? "Approve" : "Reject"} Leave Request`}
            </MDTypography>
          </DialogTitle>
          <DialogContent>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest?.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest?.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Coff Against"
                      value={selectedLeaveRequest?.AppliedOn || "No Coff Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedLeaveRequest?.reason || "No Reason Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={
                        <span>
                          Manager&#39;s Reason<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              color="secondary"
              onClick={() => {
                setActionModalOpen(false);
                setReason("");
                setSelectedLeaveRequest(null);
                setActionType("");
              }}
              variant="contained"
            >
              Cancel
            </MDButton>
            <MDButton color="primary" onClick={handleSubmitAction} variant="contained">
              Submit
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>

      <Snackbar
        open={snackOpen}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={snackColor === "success" ? "success" : "error"}
          variant="filled"
          sx={{width: "100%", color: "#fff"}}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
